import { AxiosResponse } from 'axios';

import { camelizeObject, snakizeObject } from '@cvt/helpers/object';
import { request } from '@cvt/clients/baseClient';


const apiBaseUrl = import.meta.env.VITE__CVT_API_URL;

const getPrograms = (): Promise<AxiosResponse<CVT.Query.PaginatedResults<Programs.Program>>> => {
  return request({
    options: {
      url: `${apiBaseUrl}/programs/`,
      method: 'GET',
    },
  }).then((data: AxiosResponse<CVT.Query.PaginatedResultsApi<Programs.ProgramApi>>) => ({
    ...data,
    data: {
      ...data.data,
      results: data.data.results.map(camelizeObject<Programs.Program>),
    },
  }));
};

const getProgram = (params: { id: number | string }): Promise<AxiosResponse<Programs.Program>> => {
  return request({
    options: {
      url: `${apiBaseUrl}/programs/${params.id}/`,
      method: 'GET',
    },
  }).then((data: AxiosResponse<Programs.ProgramApi>) => ({
    ...data,
    data: camelizeObject<Programs.Program>(data.data),
  }));
};

const resolveProgramLink = (params: { slug: string }): Promise<AxiosResponse<Programs.Program>> => {
  return request({
    options: {
      url: `${apiBaseUrl}/program-links/${params.slug}/`,
      method: 'GET',
    },
    authenticate: false,
  }).then((data: AxiosResponse<Programs.ProgramApi>) => ({
    ...data,
    data: camelizeObject<Programs.Program>(data.data),
  }));
};

const getEnrollments = (): Promise<AxiosResponse<CVT.Query.PaginatedResults<Programs.Enrollment>>> => {
  return request({
    options: {
      url: `${apiBaseUrl}/enrollments/`,
      method: 'GET',
    },
  }).then((data: AxiosResponse<CVT.Query.PaginatedResultsApi<Programs.EnrollmentApi>>) => ({
    ...data,
    data: {
      ...data.data,
      results: data.data.results.map(camelizeObject<Programs.Enrollment>),
    },
  }));
};

const getEnrollment = (params: { id: number | string }): Promise<AxiosResponse<Programs.Enrollment>> => {
  return request({
    options: {
      url: `${apiBaseUrl}/enrollments/${params.id}/`,
      method: 'GET',
    },
  }).then((data: AxiosResponse<Programs.EnrollmentApi>) => ({
    ...data,
    data: camelizeObject<Programs.Enrollment>(data.data),
  }));
};

const createEnrollment = (data: Programs.EnrollmentCreate): Promise<AxiosResponse<Programs.Enrollment>> => {
  return request({
    options: {
      url: `${apiBaseUrl}/enrollments/`,
      method: 'POST',
      data: snakizeObject(data),
    },
  }).then((data: AxiosResponse<Programs.EnrollmentApi>) => ({
    ...data,
    data: camelizeObject<Programs.Enrollment>(data.data),
  }));
};

const createStepEntry = (data: Programs.StepEntryCreate): Promise<AxiosResponse<Programs.StepEntry>> => {
  return request({
    options: {
      url: `${apiBaseUrl}/step-entries/`,
      method: 'POST',
      data: snakizeObject(data),
    },
  }).then((data: AxiosResponse<Programs.StepEntryApi>) => ({
    ...data,
    data: camelizeObject<Programs.StepEntry>(data.data),
  }));
};

const updateStepEntry = (data: { id: number; textResponse?: string; rating?: number | null; feedbackComment?: string }): Promise<AxiosResponse<Programs.StepEntry>> => {
  const { id, ...rest } = data;
  return request({
    options: {
      url: `${apiBaseUrl}/step-entries/${id}/`,
      method: 'PATCH',
      data: snakizeObject(rest),
    },
  }).then((data: AxiosResponse<Programs.StepEntryApi>) => ({
    ...data,
    data: camelizeObject<Programs.StepEntry>(data.data),
  }));
};

export const programClient = {
  getPrograms,
  getProgram,
  resolveProgramLink,
  getEnrollments,
  getEnrollment,
  createEnrollment,
  createStepEntry,
  updateStepEntry,
};
